import { Grid, Link, Stack } from "@mui/material";
import type { FC } from "react";
import { Link as RouterLink } from "react-router-dom";
import CustomerSelect from "../../components/customerSelect/CustomerSelect";
import Email from "../../components/links/Email";
import Phone from "../../components/links/Phone";
import { Select } from "../../components/select/Select";
import type { CustomerContactPerson } from "../../customers/customersApi";
import { useCustomers } from "../../customers/useCustomers";

interface Props {
  readonly customerId: string;
  readonly contactPersonEmail: string | undefined;
  readonly onCustomerSelect: (customerId: string) => void;
  readonly onContactPersonSelect: (email: string | undefined) => void;
}

const CustomerAndContactSelect: FC<Props> = ({
  customerId,
  contactPersonEmail,
  onCustomerSelect,
  onContactPersonSelect,
}) => {
  const { customers } = useCustomers();
  const contactPersons = customers.filter((c) => c.id === customerId).flatMap((c) => c.contacts ?? []);
  const contactPersonOptions = contactPersons
    .filter((c): c is CustomerContactPerson & { loginEmail: string } => c.loginEmail !== undefined)
    .map((c) => ({ value: c.loginEmail, label: c.name ?? c.loginEmail }));
  const contactPerson = contactPersons.find(
    (p) => p.email === contactPersonEmail || p.loginEmail === contactPersonEmail,
  );
  const accountManager = customers.find((c) => c.id === customerId)?.accountManager;

  const handleCustomerSelect = (customerId: string | null) => {
    if (customerId === null) {
      return;
    }
    onCustomerSelect(customerId);
    const defaultContactPerson = customers
      .filter((c) => c.id === customerId)
      .flatMap((c) => c.contacts ?? [])
      .map((c) => c.loginEmail)
      .at(0);
    onContactPersonSelect(defaultContactPerson);
  };

  return (
    <>
      <Grid size={6}>
        <CustomerSelect
          customerId={customerId}
          showLink
          disableClearable
          onChange={handleCustomerSelect}
          helperText={
            accountManager ? (
              <Link component={RouterLink} to={`/admin/agents/${accountManager.id}`} target="_blank">
                {accountManager.name}
              </Link>
            ) : undefined
          }
        />
      </Grid>
      <Grid size={6}>
        <Select
          fullWidth
          label="Osoba kontaktowa"
          value={contactPerson?.loginEmail ?? ""}
          values={contactPersonOptions}
          onSelect={onContactPersonSelect}
          helperText={
            <Stack direction="row" spacing={2}>
              {contactPerson?.email && <Email value={contactPerson.email} />}
              {contactPerson?.phone && <Phone value={contactPerson.phone} />}
            </Stack>
          }
        />
      </Grid>
    </>
  );
};

export default CustomerAndContactSelect;
